import React from 'react';

import EmptyState from '@atlaskit/empty-state';
import Heading from '@atlaskit/heading';
import { useMappedUsers } from 'src/services/mappedUsers';

import { trackEvent } from 'src/lib/analytics';

import CenteredSpinner from 'src/views/components/CenteredSpinner';
import ErrorMessage from 'src/views/components/ErrorMessage';

import MapUsersButton from './components/MapUsersButton';
import Search from './components/Search';
import UnlinkUsersButton from './components/UnlinkUsersButton';

import './index.css';

const onClickPDUser = () => {
  trackEvent('settings_usermappings_linkedaccounts', { label: 'navigate_to_pduser' });
};

const onClickJiraUser = () => {
  trackEvent('settings_usermappings_linkedaccounts', { label: 'navigate_to_jirauser' });
};

const UserTableItem = ({ id, pd_displayname, pd_html_url, pd_email, jira_displayname, jira_email, jira_html_url, created_by }) => {
  return (
    <tr>
      <td><a href={pd_html_url} onClick={() => onClickPDUser()} target="_blank" rel="noopener noreferrer">{pd_displayname}</a></td>
      <td>{pd_email}</td>
      <td><a href={jira_html_url} onClick={() => onClickJiraUser()} target="_blank" rel="noopener noreferrer">{jira_displayname}</a></td>
      <td>{jira_email}</td>
      <td>{created_by}</td>
      <td><UnlinkUsersButton id={id} analyticsEvent="settings_usermappings_linkedaccounts"/></td>
    </tr>
  );
};

const LimitedViewWarning = ({ shown, total }) => {
  return (
    <span style={{ color: '#FF8B00', fontSize: '0.85rem', visibility: shown < total ? 'visible' : 'hidden' }} >
          only {shown} most recently linked users is shown, {total} total
    </span>
  );
};

const Table = ({ mappedUsers }) => {
  return (
    <table className="users-mapping-table">
      <colgroup>
        <col span="1" style={{ width: '20%' }}/>
        <col span="1" style={{ width: '20%' }}/>
        <col span="1" style={{ width: '20%' }}/>
        <col span="1" style={{ width: '20%' }}/>
        <col span="1" style={{ width: '10%' }}/>
        <col span="1" style={{ width: '10%' }}/>
      </colgroup>
      <thead>
        <tr>
          <th>PagerDuty User</th>
          <th>PagerDuty User Email</th>
          <th>Jira User</th>
          <th>Jira User Email</th>
          <th>Linked by</th>
          <th/>
        </tr>
      </thead>
      <tbody>
        {mappedUsers.map(u => <UserTableItem key={u.id} {...u}/>)}
      </tbody>
    </table>
  );
};

const StatefulTableView = ({ mappedUsers, loading, error, filter, onFilterChange }) => {
  if (error) return <ErrorMessage error={['Failed to load users table', error]}/>;

  if (mappedUsers.length === 0 && filter === '' && !loading) {
    return (
      <EmptyState
        header="No linked accounts"
        description="Link users manually on this page. Users can also link their own accounts using the PagerDuty sidebar on issues"
        primaryAction={<MapUsersButton appearance="primary"/>}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <Search filter={filter} onFilterChange={onFilterChange}/>
        <MapUsersButton/>
      </div>
      {loading && <CenteredSpinner size="large"/>}
      {!loading && <Table mappedUsers={mappedUsers}/>}
    </div>
  );
};

const UsersMappingsTable = () => {
  const { mappedUsers, total, loading, error, filter, onFilterChange } = useMappedUsers();

  return (
    <div>
      <Heading size="medium">Linked user accounts</Heading>
      <LimitedViewWarning shown={mappedUsers.length} total={total}/>
      <StatefulTableView mappedUsers={mappedUsers} loading={loading} error={error} filter={filter} onFilterChange={onFilterChange}/>
    </div>
  );
};

export default UsersMappingsTable;
